import AgGridReactTable from "@components/ag-grid/AgGridReactTable";
import AgGridSkeleton from "@components/ag-grid/AgGridSkeleton";
import "@components/ag-grid/styles/AgGrid.css";
import GlobalSearchInputField from "@components/inputs/GlobalSearchInputField";
import { getApiErrorDisplay } from "@services/api/apiError";
import { primeServices } from "@services/apis/Prime";
import {
  GetPHPInstanceData,
  GetPHPInstancesRequest,
  GetPHPInstancesResponse,
} from "@services/models/Prime/GetPHPInstancesModel";
import { RowClickedEvent } from "ag-grid-community";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import secureLocalStorage from "react-secure-storage";
import webSocketService from "../../services/websocket/WebsocketServices";
import { createPhilippinesOrderColumnDefs } from "./components/PhilippinesOrderComponents";
import PrimeDetailsPhilippinesTabs from "./PrimeDetailsPhilippinesTabs";
import { getPrimeOrderAgRowClass } from "./utils/primeOrderRowClass";

const PrimeOrderFlowPhilippines = () => {
  const gridRef = useRef<any>(null);
  const ROWS_PER_PAGE = 1000;

  const [orders, setOrders] = useState<GetPHPInstanceData[]>([]);
  const [error, setError] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [instanceId, setInstanceId] = useState<string | undefined>(undefined);
  const [tradeType, setTradeType] = useState<string | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);

  const [isPanelCollapsed, setIsPanelCollapsed] = useState<boolean>(false);

  const columnDefs = useMemo(() => createPhilippinesOrderColumnDefs(), []);

  const didInitialFetch = useRef(false);
  const inFlight = useRef(false);

  const fetchOrders = async (pageNum: number, reset: boolean = false) => {
    if (inFlight.current) return;
    inFlight.current = true;

    const data: GetPHPInstancesRequest = {
      type: tradeType || undefined,
      page: pageNum,
      rows: ROWS_PER_PAGE,
      searchTerm: searchTerm.trim() || undefined,
    };

    try {
      setIsLoading(true);
      const response: GetPHPInstancesResponse =
        await primeServices.getPHPInstancesList(data);
      if (!response.data || response.data.length === 0) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }
      if (reset) {
        setOrders(response.data || []);
      } else {
        setOrders((prevOrders) => [...prevOrders, ...(response.data || [])]);
      }
    } catch (error: any) {
      setError(getApiErrorDisplay(error));
    } finally {
      setIsLoading(false);
      inFlight.current = false;
    }
  };

  useEffect(() => {
    if (!didInitialFetch.current) {
      didInitialFetch.current = true;
      setPage(1);
      setHasMore(true);
      fetchOrders(1, true);
    }
  }, []);

  useEffect(() => {
    if (!didInitialFetch.current) return;
    setPage(1);
    setHasMore(true);
    fetchOrders(1, true);
  }, [tradeType]);

  useEffect(() => {
    if (hasMore && !searchTerm) {
      fetchOrders(page, false);
    }
  }, [page, hasMore, searchTerm]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (gridRef.current?.api) {
        gridRef.current.api.refreshCells({
          columns: ["active"],
          force: true,
        });
      }
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const jwt = secureLocalStorage.getItem("jwt");
    webSocketService.connect({ token: jwt }, true);
  }, []);

  const handleTradeChangeWebSockets = (socketData: GetPHPInstanceData) => {
    // Early return if search is active
    if (searchTerm) {
      return;
    }

    // Check trade type filter
    if (tradeType && tradeType !== (socketData.isBuying ? "Payin" : "Payout")) {
      return;
    }

    setOrders((prevOrders) => {
      const existingInstanceIndex = prevOrders.findIndex(
        (order) => order.instanceId === socketData.instanceId,
      );

      if (existingInstanceIndex !== -1) {
        const updatedOrders = [...prevOrders];
        updatedOrders[existingInstanceIndex] = socketData;
        return updatedOrders;
      } else {
        if (prevOrders.length > 0) {
          const socketCreatedAt = new Date(socketData.createdAt).getTime();
          const newestCreatedAt = new Date(prevOrders[0].createdAt).getTime();

          if (socketCreatedAt > newestCreatedAt) {
            return [socketData, ...prevOrders];
          } else {
            return prevOrders;
          }
        }

        return [socketData, ...prevOrders];
      }
    });
  };

  useEffect(() => {
    const initializeWebSocket = async () => {
      try {
        webSocketService.protectedSocket?.off("php_instance_list");

        if (!searchTerm) {
          webSocketService.onMessage(
            "php_instance_list",
            true,
            handleTradeChangeWebSockets,
          );
        }
      } catch (error) {
        console.error("WebSocket initialization error:", error);
      }
    };
    initializeWebSocket();
  }, [webSocketService.protectedSocket?.connected, searchTerm, tradeType]);

  const onBodyScroll = useCallback(
    (params: any) => {
      if (!hasMore || isLoading || searchTerm) return;

      const api = params.api;
      const displayedRowCount = api.getDisplayedRowCount();
      const lastDisplayedRowIndex = api.getLastDisplayedRowIndex();

      if (
        lastDisplayedRowIndex >= displayedRowCount - 5 &&
        displayedRowCount > 0
      ) {
        setPage((prev) => prev + 1);
      }
    },
    [hasMore, isLoading, searchTerm],
  );

  const onRowClicked = (event: RowClickedEvent) => {
    const selectedData = event.data;

    const allRows = document.querySelectorAll(".ag-row");
    allRows.forEach((row) => {
      row.classList.remove("ag-row-selected-custom");
    });

    if (event.rowIndex !== null) {
      const rowElement = document.querySelector(
        `[row-index="${event.rowIndex}"]`,
      );
      if (rowElement) {
        rowElement.classList.add("ag-row-selected-custom");
      }
    }

    setInstanceId(selectedData?.instanceId);
    setIsPanelCollapsed(false);
  };

  const handleSearchSubmit = async () => {
    try {
      setIsLoading(true);
      const normalizedSearchTerm = searchTerm.trim();
      const data: GetPHPInstancesRequest = {
        type: tradeType || undefined,
        page: 1,
        rows: ROWS_PER_PAGE,
        searchTerm: normalizedSearchTerm || undefined,
      };
      const response: GetPHPInstancesResponse =
        await primeServices.getPHPInstancesList(data);
      setOrders(response.data || []);
      setHasMore(false);
    } catch (error: any) {
      setError(getApiErrorDisplay(error));
    } finally {
      setIsLoading(false);
    }
  };

  const clearSearch = () => {
    setSearchTerm("");
    setPage(1);
    setHasMore(true);
  };

  return (
    <>
      <div className="flex flex-col gap-[12px]">
        <div className="flex flex-row items-center justify-between pt-[20px]">
          <div className="flex flex-row items-center overflow-x-auto overflow-y-hidden">
            <div className="flex justify-start space-x-3 items-center w-full">
              <p className="text-text-base text-[10px] font-bold whitespace-nowrap">
                Filter by:
              </p>
              <div className="flex space-x-3">
                {["Payin", "Payout"].map((type) => (
                  <div
                    key={type}
                    onClick={
                      isLoading
                        ? undefined
                        : () =>
                            setTradeType(tradeType === type ? undefined : type)
                    }
                    className={`${
                      isLoading
                        ? "opacity-50 cursor-not-allowed"
                        : "cursor-pointer"
                    } border py-[4px] px-[16px] text-text-base text-[10px] font-medium rounded-[100px] ${
                      tradeType === type
                        ? "border-border-button-secondary bg-bg-button-secondary"
                        : "border-border-subtle bg-bg-page-body"
                    }`}
                  >
                    {type}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="flex flex-row items-center overflow-x-auto overflow-y-hidden px-[12px] gap-4">
            <div className="w-[360px] flex-shrink-0">
              <GlobalSearchInputField
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onSearch={handleSearchSubmit}
                onClear={clearSearch}
                disabled={isLoading}
                isSearchApplied={!hasMore && Boolean(searchTerm.trim())}
                disableSearchButton={!searchTerm.trim()}
                placeholder="Search Instance ID, Merchant Ref ID, Contract ID"
              />
            </div>
          </div>
        </div>
        <div className="flex flex-row items-start">
          <div
            className={`flex flex-col bg-bg-page-body py-[12px] pr-[12px] overflow-hidden ${
              instanceId && !isPanelCollapsed ? "w-3/4" : "w-full"
            }`}
            style={{ height: "calc(100vh - 200px)" }}
          >
            {isLoading && page == 1 ? (
              <AgGridSkeleton
                columnDefs={columnDefs}
                style={{ height: "100%", width: "100%" }}
              />
            ) : (
              <div className="flex flex-col h-full">
                <AgGridReactTable
                  ref={gridRef}
                  rowData={orders}
                  columnDefs={columnDefs}
                  onRowClicked={onRowClicked}
                  onBodyScroll={onBodyScroll}
                  getRowId={(params) => params.data.instanceId?.toString()}
                  getRowClass={(params) =>
                    getPrimeOrderAgRowClass("philippines", params.data)
                  }
                  error={error}
                />
                {isLoading && orders.length > 0 && (
                  <div className="flex items-center justify-center py-4 border-t border-border-subtle">
                    <div className="flex items-center space-x-2 text-text-subtle">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-border-subtle border-t-border-button-primary"></div>
                      <span className="text-sm">Loading more orders...</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          {instanceId && (
            <div
              className={`bg-bg-page-body border-l border-border-subtle transition-all duration-300 relative ${
                isPanelCollapsed ? "w-0 px-0" : "w-1/4 px-2"
              }`}
              style={{ height: "calc(100dvh - 70px)" }}
            >
              <button
                onClick={() => setIsPanelCollapsed(!isPanelCollapsed)}
                className="absolute top-1/2 -translate-y-1/2 z-20 bg-bg-button-secondary text-text-base rounded-lg shadow-lg transition-all duration-200 flex items-center justify-center group"
                style={{
                  left: isPanelCollapsed ? "calc(100% - 32px)" : "-16px",
                  width: "32px",
                  height: "60px",
                }}
                aria-label={
                  isPanelCollapsed ? "Expand panel" : "Collapse panel"
                }
              >
                <span className="material-symbols-outlined text-[20px] group-hover:scale-110 transition-transform">
                  {isPanelCollapsed ? "chevron_left" : "chevron_right"}
                </span>
              </button>

              <div
                className={`overflow-auto h-full transition-opacity duration-300 ${
                  isPanelCollapsed
                    ? "opacity-0 pointer-events-none"
                    : "opacity-100"
                }`}
              >
                <PrimeDetailsPhilippinesTabs instanceId={instanceId} />
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default PrimeOrderFlowPhilippines;
